<template>
  <div class="product-details-page">

    <!-- 产品详情内容 -->
    <div class="product-details-content" v-if="product">
      <div class="content-container">
        <div class="product-main">

          <!-- 产品信息 -->
          <div class="product-info-section">
            <h1 class="product-title">{{ product.title }}</h1>
            <div class="product-meta">
              <span class="product-date">发布日期：{{ formatDate(product.date) }}</span>
            </div>
            <div class="product-description">
              <div class="product-detail-content" v-html="product.content || product.description"></div>
            </div>
          </div>
        </div>

        <!-- 相关产品推荐 -->
        <div class="related-products" v-if="relatedProducts.length > 0">
          <h3>相关产品</h3>
          <div class="related-grid">
            <div 
              v-for="relatedProduct in relatedProducts" 
              :key="relatedProduct.id"
              class="related-card"
              @click="viewProduct(relatedProduct.categoryId)"
            >
              <img :src="relatedProduct.thumbnail" :alt="relatedProduct.title" />
              <h4>{{ relatedProduct.title }}</h4>
              <p>{{ formatDate(relatedProduct.date) }}</p>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 加载状态 -->
    <div v-else-if="loading" class="loading-section">
      <div class="loading-spinner"></div>
      <p>加载中...</p>
    </div>

    <!-- 错误状态 -->
    <div v-else class="error-section">
      <h3>产品不存在</h3>
      <p>抱歉，您访问的产品不存在或已被删除。</p>
    </div>

    <!-- Footer组件 -->
    <Footer />
  </div>
</template>

<script>
import Footer from '../components/Home/Footer.vue'

export default {
  name: 'ProductDetails',
  components: {
    Footer
  },
  props: {
    productId: {
      type: [String, Number],
      required: true
    }
  },
  data() {
    return {
      loading: true,
      product: null,
      relatedProducts: []
    };
  },
  mounted() {
    this.loadProduct();
  },
  watch: {
    productId() {
      this.loadProduct();
    },
    '$route'(to, from) {
      // 仅在路径前缀发生变化（news/product/platform 切换）或实际路径变化时才重新加载
      const getPrefix = (r) => {
        const p = (r && r.path) || ''
        if (typeof p !== 'string') return ''
        if (p.startsWith('/news')) return 'news'
        if (p.startsWith('/product')) return 'product'
        if (p.startsWith('/platform')) return 'platform'
        return ''
      }
      const toPrefix = getPrefix(to)
      const fromPrefix = getPrefix(from)

      // 路径完全相同（仅 query/hash 变化）时不触发
      const samePath = (to && from) && (to.path === from.path)
      if (samePath) return

      // 前缀变化时需要重新决定接口
      if (toPrefix !== fromPrefix) {
        this.loadProduct()
        return
      }

      // 其他路径变化（如从 /product/detail/55 -> /product/detail/66）由 productId watcher 处理，无需重复
    }
  },
  methods: {
    async loadProduct() {
      this.loading = true;
      const productId = parseInt(this.productId);
      
      try {
        const routeName = (this.$route && this.$route.name) || ''
        const routePath = (this.$route && this.$route.path) || ''
        const isNews = (typeof routeName === 'string' && routeName.startsWith('news')) || (typeof routePath === 'string' && routePath.startsWith('/news'))
        const endpoint = isNews 
          ? `/api/content/${encodeURIComponent(productId)}`
          : `/api/content/detail/${encodeURIComponent(productId)}`
        const res = await this.$http.get(endpoint)
        if (res && res.data && res.data.success && res.data.data) {
          const d = res.data.data
          // 规范化为本组件使用的字段结构
          this.product = {
            ...d,
            date: d.publishTime || d.updatedAt || d.createdAt,
            description: d.seoDescription || d.subtitle,
            content: d.markdown
          }
        } else {
          this.product = null
        }
        if (this.product) {
          this.loadRelatedProducts();
        }
      } catch (error) {
        console.error('加载产品详情失败:', error);
        this.$message.error('加载产品详情失败');
      } finally {
        this.loading = false;
      }
    },
    loadRelatedProducts() {
      if (!this.product) return;

      const relationshipContents = (this.product.relationshipContents || []).filter(Boolean)
      const fallbackThumb = require('../assets/img/logo.png')

      this.relatedProducts = relationshipContents
        .filter(item => item && item.id !== this.product.id)
        .map(item => {
          const date = item.publishTime || item.updatedAt || item.createdAt
          const thumbnail = item.coverImage || fallbackThumb
          return {
            id: item.id,
            title: item.title,
            date,
            thumbnail,
            categoryId: item.categoryId
          }
        })
        .slice(0, 3)
    },
    formatDate(dateString) {
      const date = new Date(dateString);
      return date.toLocaleDateString('zh-CN');
    },
    viewProduct(productId) {
      window.open('/product/detail/' + productId, '_blank');
    },
  }
};
</script>

<style lang="scss">
@import '../assets/scss/productDetailContent.scss';
</style>

<style lang="scss" scoped>
.product-details-page {
  width: 100%;
  min-height: 100vh;
  background-color: #f5f5f5;
}


// 产品详情内容
.product-details-content {
  .content-container {
    // max-width: 1200px;
    margin: 0 auto;
    padding: 0 20px;
    
  }
  
  .product-main {
    background: white;
    border-radius: 8px;
    // max-width: 80%;
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
    overflow: hidden;
    margin-bottom: 40px;
    
    .product-info-section {
      padding: 40px;
      
      .product-title {
        font-size: 28px;
        font-weight: 600;
        color: #333;
        margin: 0 0 20px 0;
        line-height: 1.4;
      }
      
      .product-meta {
        display: flex;
        gap: 30px;
        margin-bottom: 30px;
        padding-bottom: 20px;
        border-bottom: 1px solid #f0f0f0;
        
        .product-date,
        .product-category {
          font-size: 14px;
          color: #666;
        }
      }
      
      .product-description {
        max-width: 100%;
        margin-bottom: 30px;
        
        h3 {
          font-size: 20px;
          font-weight: 600;
          color: #333;
          margin: 0 0 15px 0;
        }
        
        p {
          font-size: 16px;
          line-height: 1.8;
          color: #666;
          margin: 0;
        }
      }
      
      .product-actions {
        display: flex;
        gap: 15px;
        
        .contact-btn {
          padding: 12px 24px;
          border: none;
          border-radius: 5px;
          cursor: pointer;
          font-size: 16px;
          transition: all 0.3s ease;
          background: #6c5ce7;
          color: white;
          
          &:hover {
            background: #5a4fcf;
          }
        }
      }
    }
  }
  
  .related-products {
    background: white;
    border-radius: 8px;
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
    padding: 30px;
    
    h3 {
      font-size: 24px;
      font-weight: 600;
      color: #333;
      margin: 0 0 20px 0;
    }
    
    .related-grid {
      display: grid;
      grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
      gap: 20px;
      
      .related-card {
        cursor: pointer;
        transition: transform 0.3s ease;
        
        &:hover {
          transform: translateY(-5px);
        }
        
        img {
          width: 100%;
          height: 150px;
          object-fit: cover;
          border-radius: 5px;
          margin-bottom: 10px;
        }
        
        h4 {
          font-size: 16px;
          font-weight: 600;
          color: #333;
          margin: 0 0 5px 0;
          line-height: 1.4;
        display: -webkit-box;
        -webkit-line-clamp: 2;
        line-clamp: 2;
        -webkit-box-orient: vertical;
        overflow: hidden;
        }
        
        p {
          font-size: 14px;
          color: #666;
          margin: 0;
        }
      }
    }
  }
}

// 加载和错误状态
.loading-section,
.error-section {
  text-align: center;
  padding: 60px 20px;
  
  .loading-spinner {
    width: 40px;
    height: 40px;
    border: 4px solid #f3f3f3;
    border-top: 4px solid #6c5ce7;
    border-radius: 50%;
    animation: spin 1s linear infinite;
    margin: 0 auto 20px;
  }
  
  h3 {
    font-size: 24px;
    color: #333;
    margin: 0 0 10px 0;
  }
  
  p {
    font-size: 16px;
    color: #666;
    margin: 0 0 20px 0;
  }
  
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

// 响应式设计
@media (max-width: 768px) {
  .product-details-content {
    .product-main {
      .product-info-section {
        padding: 20px;
        
        .product-title {
          font-size: 24px;
        }
        
        .product-meta {
          flex-direction: column;
          gap: 10px;
        }
        
        .product-actions {
          flex-direction: column;
          
          .contact-btn,
          .back-btn {
            width: 100%;
          }
        }
      }
    }
    
    .related-products {
      padding: 20px;
      
      .related-grid {
        grid-template-columns: 1fr;
      }
    }
  }
  
  .banner-section {
    height: 200px;
    
    .banner-content {
      .banner-title {
        font-size: 32px;
      }
    }
  }
}

@media (max-width: 480px) {
  .banner-section {
    .banner-content {
      .banner-title {
        font-size: 24px;
      }
      
      .breadcrumb {
        font-size: 14px;
      }
    }
  }
}
</style>
